import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { api } from '../../lib/api';

interface Props {
  // Solo se activa para usuarios recién incorporados (evita sondear siempre).
  active: boolean;
}

interface Status { pending: number; succeeded: number; failed: number; total: number }

const POLL_MS = 5000;
const MAX_POLLS = 36; // ~3 min de margen y se detiene solo

/**
 * Indicador no bloqueante (Fase 4). Mientras quedan imágenes del onboarding
 * generándose en segundo plano, muestra "tu sitio se está terminando de vestir"
 * y se autodescarta cuando ya no hay nada pendiente. Si nunca hubo imágenes o
 * el entorno no las genera, no aparece.
 */
export default function SiteDressingBanner({ active }: Props) {
  const [status, setStatus] = useState<Status | null>(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!active) return;
    let timer: ReturnType<typeof setTimeout>;
    let stopped = false;

    const tick = async () => {
      try {
        const s = await api.ai.siteImagesStatus();
        if (stopped) return;
        setStatus(s);
        // Detener cuando no queda nada pendiente o tras el máximo de sondeos.
        if (s.pending === 0 || ++pollsRef.current >= MAX_POLLS) return;
      } catch {
        if (++pollsRef.current >= MAX_POLLS) return;
      }
      timer = setTimeout(tick, POLL_MS);
    };

    tick();
    return () => { stopped = true; clearTimeout(timer); };
  }, [active]);

  const show = !!status && status.pending > 0 && status.total > 0;

  return (
    <AnimatePresence>
      {show && status && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="flex items-center gap-3 rounded-xl border border-ui-border bg-ui-surface px-4 py-3"
        >
          <span className="flex-shrink-0 grid place-items-center w-9 h-9 rounded-full bg-ui-accent/15 text-ui-accent">
            <Sparkles size={18} className="animate-pulse" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-ui-text">Estamos terminando de vestir tu sitio</p>
            <p className="text-sm text-ui-text-muted">
              {status.succeeded} de {status.total} imágenes listas. Puedes seguir trabajando mientras tanto.
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
